import { requestPageFullscreen, exitPageFullscreen } from "@onmo/ui";
import { isAndroidApp, isTV } from "../utils/platform";
import {
  MAX_WIDTH_MOBILE,
  MAX_WIDTH_MOBILE_LANDSCAPE,
  MAX_WIDTH_TABLET,
} from "../constants/dimension";
import { Devices } from "../types/screen";

const _supportedStyles = [
  "width",
  "height",
  "min-width",
  "max-width",
  "min-height",
  "max-height",
  "font-size",
  "padding",
  "margin",
  "border-radius",
  "border-width",
  "line-height",
  "letter-spacing",
  "top",
  "right",
  "bottom",
  "left",
  "inset-inline-start",
  "inset-inline-end",
  "padding-inline-start",
  "padding-inline-end",
  "margin-inline-start",
  "margin-inline-end",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "border-bottom",
  "border-top",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "border-top-left-radius",
  "border-top-right-radius",
  "border-bottom-right-radius",
  "border-bottom-left-radius",
  "grid-gap",
  "gap",
  "border",
  "outline",
  "outline-offset",
  "scroll-padding-inline",
  "scroll-margin-block-start",
  "filter",
  "inset",
] as const;

// create a type from supportedStylesArray
type SupportedStyles = (typeof _supportedStyles)[number];

const isSupportedStyle = (style: string) => {
  return _supportedStyles.includes(style as SupportedStyles);
};

type StyleMap = {
  [K in SupportedStyles]: number | string;
};

const MIN_FONT_SIZE = 10;

const scaleFontSize = (key: string, value: number) =>
  key === "font-size" ? Math.max(MIN_FONT_SIZE, value) : value;

export const mediaQueryScaleStyles = (styles: Partial<StyleMap>) => {
  const screenRate = getMediaSizeScale();
  const composedStyles = {};

  for (const [k, v] of Object.entries(styles)) {
    if (v === undefined) {
      continue;
    }
    if (isSupportedStyle(k)) {
      if (typeof v === "string") {
        const styles = v.split(" ").map((style) => {
          if (style.includes("px")) {
            if (style.startsWith("calc(")) {
              const numStr = style.slice(5, -1);
              const num = parseFloat(numStr) * screenRate;
              return `calc(${num}px`;
            } else {
              const num = scaleFontSize(k, parseFloat(style) * screenRate);
              return style.endsWith("px)") ? `${num}px)` : `${num}px`;
            }
          }
          return style;
        });
        (composedStyles as { [key: string]: number | string })[k] =
          styles.join(" ");
      } else if (typeof v === "number") {
        (composedStyles as { [key: string]: number | string })[k] =
          scaleFontSize(k, v * screenRate);
      }
    } else {
      (composedStyles as { [key: string]: number | string })[k] = v;
    }
  }
  return composedStyles;
};

export const getMediaSizeScale = () => {
  const isDesktop = window.innerWidth > MAX_WIDTH_TABLET;
  const isTabletLandscape =
    window.innerWidth > window.innerHeight &&
    window.innerWidth > MAX_WIDTH_MOBILE_LANDSCAPE;

  if (isDesktop || isTabletLandscape) {
    const defaultWidth = 1920;
    return window.innerWidth / defaultWidth;
  }

  return 1;
};

const getDistanceElement = (
  currentElement: HTMLElement,
  otherElement: HTMLElement,
  action: "left" | "right" | "up" | "down",
) => {
  let distanceWidth = 0;
  let distanceHeight = 0;

  const current = currentElement.getBoundingClientRect();
  const other = otherElement.getBoundingClientRect();

  if (action === "left") {
    distanceWidth = current.left - other.left - other.width;
    distanceHeight = current.top - other.top;
  } else if (action === "right") {
    distanceWidth = other.left - current.left - current.width;
    distanceHeight = current.top - other.top;
  } else if (action === "up") {
    distanceWidth = current.left - other.left;
    distanceHeight = current.top - other.top - other.height;
  } else if (action === "down") {
    distanceWidth = current.left - other.left;
    distanceHeight = other.top - current.top - current.height;
  }

  return Math.sqrt(
    distanceWidth * distanceWidth + distanceHeight * distanceHeight,
  );
};

export const findClosestElement = (
  currentElement: HTMLElement,
  action: "left" | "right" | "up" | "down",
  listElements?: HTMLElement[],
) => {
  if (!listElements?.length) return undefined;

  return listElements.reduce(
    (prev, curr) =>
      getDistanceElement(currentElement, prev, action) >
      getDistanceElement(currentElement, curr, action)
        ? curr
        : prev,
    listElements[0],
  );
};
export const findElementWithoutMenu = (
  currentElement: HTMLElement,
  action: "left" | "right" | "up" | "down",
  listElements?: HTMLElement[],
) => {
  if (!listElements?.length) return undefined;
  const filtered = listElements.filter(
    (element) => !element.getAttribute("data-gamepad")?.startsWith("menu-"),
  );

  if (!filtered.length) return undefined;
  return filtered.reduce(
    (prev, curr) =>
      getDistanceElement(currentElement, prev, action) >
      getDistanceElement(currentElement, curr, action)
        ? curr
        : prev,
    filtered[0],
  );
};

export const findStartElementByClientRect = (listElements?: HTMLElement[]) => {
  if (!listElements?.length) return undefined;

  let result: HTMLElement | undefined;
  let minX = Infinity;

  for (const el of listElements) {
    const x = el.getBoundingClientRect().x;
    if (x > 0 && x < minX) {
      minX = x;
      result = el;
    }
  }

  return result;
};

export const findStartElementRectById = (
  id: string,
  listElements?: HTMLElement[],
) => {
  if (!listElements?.length) return undefined;
  const elements = listElements.filter((el) =>
    el.getAttribute("data-gamepad")?.startsWith(id),
  );
  return findStartElementByClientRect(elements);
};

export const findNextRowElement = (
  currentElement: HTMLElement,
  action: "up" | "down",
  listElements?: HTMLElement[],
) => {
  if (!listElements?.length) return undefined;

  let closest: HTMLElement | undefined;
  let minDistance = Infinity;
  for (const el of listElements) {
    if (el === currentElement) continue;

    const dy = el.offsetTop - currentElement.offsetTop;

    if (action === "up" && dy >= 0) continue;
    if (action === "down" && dy <= 0) continue;

    const distance = Math.abs(dy);
    if (distance < minDistance) {
      minDistance = distance;
      closest = el;
    }
  }

  return listElements.filter(
    (el) =>
      el?.getAttribute("data-gamepad") ===
      closest?.getAttribute("data-gamepad"),
  );
};

export const findNextElementByRowId = (
  currentElement: HTMLElement,
  action: "left" | "right",
  listElements?: HTMLElement[],
) => {
  const currentRowId = currentElement.getAttribute("data-gamepad");
  const currentRow = listElements?.filter(
    (el) => el.getAttribute("data-gamepad") === currentRowId,
  );
  const currentElementIndex = currentRow?.findIndex(
    (el) => el === currentElement,
  );

  if (!currentRow) return undefined;
  return currentRow[(currentElementIndex || 0) + (action === "left" ? -1 : 1)];
};

export const findNextRowElementByRect = (
  currentElement: HTMLElement,
  action: "up" | "down" | "left" | "right",
  listElements?: HTMLElement[],
) => {
  if (!listElements?.length || action === "left" || action === "right")
    return undefined;

  const currentRect = currentElement.getBoundingClientRect();

  let closest: HTMLElement | undefined;
  let minDistance = Infinity;
  for (const el of listElements) {
    if (el === currentElement) continue;
    const rect = el.getBoundingClientRect();
    const dy = rect.top - currentRect.top;

    if (action === "up" && dy >= 0) continue;
    if (action === "down" && dy <= 0) continue;

    const distance = Math.abs(dy);
    if (distance < minDistance) {
      minDistance = distance;
      closest = el;
    }
  }

  return listElements.filter(
    (el) =>
      el?.getAttribute("data-gamepad") ===
      closest?.getAttribute("data-gamepad"),
  );
};

export const getAllElementBySide = (
  currentElement: HTMLElement,
  action: "left" | "right" | "up" | "down",
  listElements?: HTMLElement[],
) => {
  if (!listElements?.length) return undefined;
  const current = currentElement.getBoundingClientRect();
  if (action === "left") {
    return listElements.filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.left < current.left && rect.right < current.right;
    });
  } else if (action === "right") {
    return listElements.filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.left > current.left && rect.right > current.right;
    });
  } else if (action === "up") {
    return listElements.filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.top < current.top && rect.bottom < current.bottom;
    });
  } else if (action === "down") {
    return listElements.filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.top > current.top && rect.bottom > current.bottom;
    });
  }
  return [];
};

export const getDeviceType = (): "Mobile" | "Tablet" | "TV" | "Desktop" => {
  const ua = navigator.userAgent;

  if (/mobile/i.test(ua)) return "Mobile";
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "Tablet";
  if (/smart-tv|hbbtv|appletv|googletv|roku|netcast|viera|aquos/i.test(ua))
    return "TV";
  return "Desktop";
};

export const isTopButton = (item: HTMLElement) => {
  // ignore scroll center on game detail
  const dataGamepad = item.getAttribute("data-gamepad");
  if (!dataGamepad) return false;

  const isTopButton = ["button-play", "button-active"].includes(dataGamepad);
  return isTopButton;
};

export const getElementOffsetBottom = (item: HTMLElement) => {
  return item.offsetTop + item.offsetHeight - window.innerHeight;
};

export const requestFullscreenApp = () => {
  if (isTV || isAndroidApp) return;
  (navigator as any).keyboard?.lock?.(["Escape"]);
  requestPageFullscreen();
};

export const getFullscreenElement = () => {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
};

export const exitFullscreenApp = async () => {
  if (isTV) return;
  (navigator as any).keyboard?.unlock?.(["Escape"]);
  exitPageFullscreen();
};

export const requestPointerLock = async (element?: Element) => {
  if (document.pointerLockElement) return;

  const targetEl = element ?? document.documentElement;
  try {
    await targetEl.requestPointerLock();
  } catch (error) {
    console.error("Failed to request pointer lock", error);
  }
};

export const isElementVisible = (
  element: HTMLElement | null | undefined,
): boolean => {
  if (!element) return false;

  // Check if element has checkVisibility() method
  if (typeof element.checkVisibility === "function") {
    return element.checkVisibility();
  }

  // fallback to check if element is in DOM
  if (!element.isConnected) return false;

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  return true;
};

export const getDevice = (): Devices => {
  if (window.innerWidth > MAX_WIDTH_TABLET) return Devices.Desktop;
  if (window.innerWidth <= MAX_WIDTH_MOBILE) return Devices.Mobile;
  return Devices.Tablet;
};
